import React, { useState, useRef, useEffect, useMemo } from 'react';
import AvatarEditorModal from './AvatarEditorModal';
import useUpdateCandidateProfile from '../api/useUpdateCandidateProfile';
import usePostCandidateProfilesAvatar from '../api/usePostCandidateProfilesAvatar';
import { useForm } from 'react-hook-form';
import { BlockButton } from '@/components/BlockButton';
import type { CandidateProfileResponse } from '../types/CandidateProfile';
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import { TextField } from '@/components/TextField';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import useFetchCandidateProfilesCustomFields from '../api/useFetchCandidateProfilesCustomFields';
import {
  normalizeCustomFieldsByCategory,
  type CustomFieldsByCategory,
} from '../../../../utils/normalizeCustomFieldsByCategory';
import CustomFieldsForm from './CustomFieldsForm';
import { type CustomFieldValue, isCustomFieldValueEmpty } from '@/utils/customFieldValidation';
import { buildCustomDataPayload } from '@/utils/customDataForm';

type FormValues = {
  customData: Record<string, string>;
};

type Props = {
  candidateId: number;
  candidateName: string;
  customData: CandidateProfileResponse['custom_data'];
  organizationUserIds: number[];
  avatarUrl: string;
  candidateProfileRefetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<CandidateProfileResponse, Error>>;
  onEditCancel: () => void;
};

/**
 * 求職者プロフィールカード編集モード
 */
export default function CandidatesProfileCardEdit({
  candidateId,
  candidateName,
  customData,
  organizationUserIds,
  avatarUrl,
  candidateProfileRefetch,
  onEditCancel,
}: Props) {
  const [name, setName] = useState(candidateName);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarImage, setAvatarImage] = useState(avatarUrl);
  // カスタムフィールド単位のバリデーションエラー（key: field.id の文字列, value: エラーメッセージ）
  const [customFieldErrors, setCustomFieldErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { register, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      customData: customData?.reduce(
        (acc, data) => {
          acc[String(data.custom_field.id)] = data.content;
          return acc;
        },
        {} as Record<string, string>
      ),
    },
  });
  const { mutateAsync: updateProfile } = useUpdateCandidateProfile();
  const { mutateAsync: updateAvatar } = usePostCandidateProfilesAvatar();
  const { data: candidateCustomFields } = useFetchCandidateProfilesCustomFields();

  const onInputName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setAvatarImage(event.target.result as string);
          setShowAvatarModal(true);
        }
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleSaveAvatar = async (croppedImage: string) => {
    if (!croppedImage) {
      return;
    }

    const base64Data = croppedImage.split(',')[1];
    const binaryData = atob(base64Data);
    const array = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      array[i] = binaryData.charCodeAt(i);
    }
    const blob = new Blob([array], { type: 'image/png' });
    const file = new File([blob], 'avatar.png', { type: 'image/png' });
    setSelectedFile(file);

    const img = new Image();
    img.onload = () => {
      setAvatarImage(croppedImage);
      setShowAvatarModal(false);
    };
    img.onerror = () => {
      setShowAvatarModal(false);
    };
    img.src = croppedImage;
  };

  const handleCancelAvatar = () => {
    setAvatarImage(avatarUrl);
    setShowAvatarModal(false);
  };

  // 求職者名は RHF 管理外（ローカル state）のため、ここで必須チェックする
  const isNameEmpty = name.trim() === '';

  const onSubmit = async (data: FormValues) => {
    // 求職者名の必須チェック（空なら送信を止める）
    if (name.trim() === '') {
      showErrorToast('入力内容に不備があります。エラー表示のある項目を確認してください');
      return;
    }

    // 必須カスタムフィールドの送信前バリデーション
    const newErrors: Record<string, string> = {};
    if (candidateCustomFields?.records) {
      for (const field of candidateCustomFields.records) {
        if (!field.is_required) continue;
        const value = customFieldValues[String(field.id)];
        if (isCustomFieldValueEmpty(field, value)) {
          newErrors[String(field.id)] = `${field.name}は必須項目です`;
        }
      }
    }
    setCustomFieldErrors(newErrors);
    // 1件でもエラーがあれば送信を止める
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // undefined（既存データなし且つ未変更）は送信しない＝既存維持、
    // 空文字列はユーザーが明示的に削除した状態なのでそのまま送信（バックエンドで削除処理）
    const customDataReq = buildCustomDataPayload(data.customData, 'update');

    try {
      if (selectedFile) {
        await updateAvatar({
          candidateProfileId: candidateId,
          file: selectedFile,
        });
      }

      await updateProfile({
        candidateProfileId: candidateId,
        name: name,
        custom_data: customDataReq,
        organization_user_ids: organizationUserIds,
      });
      onEditCancel();
      await candidateProfileRefetch();
      showSuccessToast('更新しました');
    } catch (error) {
      // error.message にバックエンドから返ったエラーメッセージが入っている
      // react-toastify は \n をそのまま表示しないため「、」区切りに変換する
      // 300 文字以上の場合は最初の区切りまでを表示（過度に長いメッセージを防止）
      const MAX_MESSAGE_LENGTH = 300;
      let toastMessage = '更新に失敗しました';
      if (error instanceof Error && error.message) {
        const rawMessage = error.message;
        if (rawMessage.length > MAX_MESSAGE_LENGTH) {
          // 最初の改行までを使用し、残りを省略
          // 改行がない単一行ロングメッセージでも必ず MAX_MESSAGE_LENGTH に収める
          const firstLine = rawMessage.split('\n')[0] || rawMessage;
          toastMessage =
            firstLine.length > MAX_MESSAGE_LENGTH
              ? `${firstLine.slice(0, MAX_MESSAGE_LENGTH)}…`
              : firstLine;
        } else {
          // \n を「、」区切りに変換して1行に収める
          toastMessage = rawMessage.split('\n').join('、');
        }
      }
      showErrorToast(toastMessage);
    }
  };

  useEffect(() => {
    setName(candidateName);
  }, [candidateName]);

  const normalizedCustomFields = normalizeCustomFieldsByCategory(candidateCustomFields?.records);

  const profileCategory = normalizedCustomFields.find(
    (category) => category.categoryName === 'プロフィール'
  );
  const otherCategories = normalizedCustomFields.filter(
    (category) => category.categoryName !== 'プロフィール'
  );

  // CustomFieldsForm用のvaluesを構築（フィールドIDをキーとして）
  // react-hook-formのwatchを使って動的に値を取得
  const formValues = watch();
  const customFieldValues = useMemo(() => {
    const values: Record<string, CustomFieldValue> = {};
    candidateCustomFields?.records.forEach((field) => {
      const formValue = formValues.customData?.[String(field.id)] as string | undefined;

      if (formValue === undefined) {
        // フォームに未登録の場合のみcustomDataから初期値を取得
        const existingData = customData?.find((data) => data.custom_field.id === field.id);
        if (existingData) {
          let value: CustomFieldValue = existingData.content;
          // multiple_selectの場合は配列に変換
          if (field.field_type === 'multiple_select' && typeof existingData.content === 'string') {
            value = existingData.content
              .split(',')
              .map((v) => v.trim())
              .filter((v) => v !== '');
          } else if (field.field_type === 'number' && typeof existingData.content === 'string') {
            value = existingData.content ? Number(existingData.content) : undefined;
          }
          values[String(field.id)] = value;
        }
      } else if (formValue !== '') {
        // react-hook-formから値を取得（空文字列はユーザーが削除した状態なので無視）
        let value: CustomFieldValue = formValue;
        // multiple_selectの場合は配列に変換
        if (field.field_type === 'multiple_select' && typeof formValue === 'string') {
          value = formValue
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v !== '');
        } else if (field.field_type === 'number' && typeof formValue === 'string') {
          value = formValue ? Number(formValue) : undefined;
        }
        values[String(field.id)] = value;
      }
    });
    return values;
  }, [formValues, customData, candidateCustomFields?.records]);

  // CustomFieldsFormのonChangeハンドラー
  const handleCustomFieldChange = (fieldId: number, value: CustomFieldValue) => {
    // react-hook-formに値を設定（既存の形式に合わせる）
    const stringValue = Array.isArray(value)
      ? value.join(',')
      : value !== undefined
        ? String(value)
        : '';
    setValue(`customData.${fieldId}`, stringValue);

    // 値が変更されたフィールドのバリデーションエラーをクリア
    if (customFieldErrors[String(fieldId)]) {
      setCustomFieldErrors((prev) => {
        const next = { ...prev };
        delete next[String(fieldId)];
        return next;
      });
    }
  };

  /**
   * カテゴリーフィールド編集用レンダリング
   */
  const renderCategoryFieldsEdit = (category: CustomFieldsByCategory) => {
    // CustomFieldsForm用にSharedCustomField[]を取得
    const categoryFieldIds = category.customFields.map((field) => field.fieldId);
    const sharedFields = candidateCustomFields?.records
      .filter((field) => categoryFieldIds.includes(field.id))
      .sort((a, b) => a.order_number - b.order_number);

    if (!sharedFields || sharedFields.length === 0) {
      return null;
    }

    return (
      <div className="mt-7">
        <CustomFieldsForm
          fields={sharedFields}
          values={customFieldValues}
          onChange={handleCustomFieldChange}
          registerHiddenInput={(name) => register(name)}
          errors={customFieldErrors}
        />
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="relative flex flex-col gap-6">
      <div className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]">
        <div className="flex items-center gap-4">
          <p className="text-lg font-bold text-primary">プロフィール</p>
        </div>
        <div className="mt-6 flex items-center gap-6">
          <div className="relative cursor-pointer hover:opacity-60">
            <img
              src={
                avatarImage
                  ? avatarImage
                  : avatarUrl === ''
                    ? '/agent/default-avatar.png'
                    : avatarUrl
              }
              alt=""
              width={72}
              height={72}
              className="cursor-pointer rounded-full"
              onClick={() => fileInputRef.current?.click()}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <div
              className="absolute inset-0 flex items-center justify-center"
              onClick={() => fileInputRef.current?.click()}
            >
              <img src={'/agent/candidates/icon-avatar-change.svg'} alt="" width={33} height={33} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex w-full flex-col gap-2">
              <label htmlFor="candidateName" className="text-sm font-bold">
                求職者名
                <span className="ml-2 inline-flex h-5 items-center rounded-full bg-danger_lighten px-3 text-xs leading-none font-bold text-danger">
                  必須
                </span>
              </label>
              <div>
                <TextField
                  name="candidateName"
                  defaultValue={name}
                  onChange={onInputName}
                  error={isNameEmpty}
                />
                {isNameEmpty && (
                  <p role="alert" className="mt-xxs text-body-xs text-danger">
                    ＊求職者名は必須項目です。入力してください。
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        {profileCategory && renderCategoryFieldsEdit(profileCategory)}
      </div>

      {/* カスタムフィールドセクション（プロフィール以外） */}
      {otherCategories.map((category) => (
        <div
          key={category.categoryId}
          className="bg-white px-10 pt-8 pb-9 shadow-[0px_0px_6px_0px_#00000033]"
        >
          <div className="flex items-center gap-4">
            <p className="text-lg font-bold text-primary">{category.categoryName}</p>
          </div>
          {renderCategoryFieldsEdit(category)}
        </div>
      ))}

      {showAvatarModal && (
        <div className="fixed inset-0 z-[1000]">
          <AvatarEditorModal
            src={avatarImage}
            onClose={handleCancelAvatar}
            onSave={handleSaveAvatar}
          />
        </div>
      )}
      <div className="fixed right-6 bottom-6 w-[96px]">
        <BlockButton
          text={'保存する'}
          height={52}
          fontSize="text-base"
          type="submit"
          disabled={isNameEmpty || Object.keys(customFieldErrors).length > 0}
        />
      </div>
    </form>
  );
}
